import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Check, X, Shield, MapPin, Clock, Package } from "lucide-react";

const TABS = ["pending", "approved", "rejected"];

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [apps, setApps] = useState(null);

  useEffect(() => {
    if (!loading) {
      if (!user) { navigate("/"); return; }
      if (!user.is_admin) { toast.error("Admin only"); navigate("/"); return; }
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user?.is_admin) return;
    setApps(null);
    api.get("/admin/maker-applications", { params: { status: tab } })
      .then(({ data }) => setApps(data))
      .catch((err) => { console.error(err); setApps([]); });
  }, [tab, user]);

  const approve = async (appId) => {
    try {
      await api.post(`/admin/maker-applications/${appId}/approve`);
      setApps((a) => a.filter((x) => x.id !== appId));
      toast.success("Approved — added to The Bureau");
    } catch (err) { console.error(err); toast.error("Failed"); }
  };
  const reject = async (appId) => {
    try {
      await api.post(`/admin/maker-applications/${appId}/reject`);
      setApps((a) => a.filter((x) => x.id !== appId));
      toast.success("Rejected");
    } catch (err) { console.error(err); toast.error("Failed"); }
  };

  if (loading || !user?.is_admin) return <div className="min-h-screen bg-zinc-950" />;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 pt-24 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
          <div className="md:col-span-8">
            <div className="overline text-lime-400 mb-3 flex items-center gap-2"><Shield className="w-3 h-3" /> § Admin — Bureau Review</div>
            <h1 className="font-serif text-6xl md:text-8xl leading-[0.9]">Curate<br /><em className="italic font-light">the Bureau.</em></h1>
          </div>
          <div className="md:col-span-4 flex md:justify-end items-end">
            <div className="flex gap-1" data-testid="admin-tabs">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-4 py-2 overline border ${tab === t ? "bg-lime-400 text-zinc-950 border-lime-400" : "border-zinc-800 hover:border-lime-400"} transition-colors`}
                  data-testid={`admin-tab-${t}`}
                >{t}</button>
              ))}
            </div>
          </div>
        </div>

        {apps === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-lime-400" /></div>
        ) : apps.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-16 text-center">
            <div className="font-serif italic text-3xl text-zinc-500">Nothing {tab}.</div>
          </div>
        ) : (
          <div className="space-y-1" data-testid="admin-applications-list">
            {apps.map((a) => (
              <div key={a.id} className="border-t border-zinc-800 py-6 grid grid-cols-1 md:grid-cols-12 gap-6" data-testid={`admin-app-${a.id}`}>
                <div className="md:col-span-6">
                  <div className="overline text-zinc-500 mb-1">{a.tier}</div>
                  <div className="font-serif text-3xl">{a.name}</div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-zinc-500">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{a.city}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{a.lead_time_days}d</span>
                    <span className="flex items-center gap-1"><Package className="w-3 h-3" />MOQ {a.moq}</span>
                    <span className="font-mono">{a.price_range}</span>
                  </div>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{a.bio}</p>
                  <div className="mt-2 text-xs text-zinc-500 font-mono">{a.email}</div>
                  {a.portfolio_url && (
                    <a href={a.portfolio_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-lime-400 hover:underline break-all" data-testid={`admin-portfolio-${a.id}`}>{a.portfolio_url}</a>
                  )}
                </div>
                <div className="md:col-span-3">
                  <div className="overline text-zinc-500 mb-2">Specialties</div>
                  <div className="flex flex-wrap gap-1">
                    {a.specialties?.map((s) => <span key={s} className="text-[10px] uppercase tracking-widest px-2 py-1 border border-zinc-700">{s}</span>)}
                  </div>
                </div>
                {tab === "pending" && (
                  <div className="md:col-span-3 flex flex-col gap-2 md:items-end">
                    <button
                      onClick={() => approve(a.id)}
                      className="px-5 py-2 bg-lime-400 text-zinc-950 overline hover:bg-lime-300 flex items-center gap-2"
                      data-testid={`admin-approve-${a.id}`}
                    ><Check className="w-4 h-4" /> Approve</button>
                    <button
                      onClick={() => reject(a.id)}
                      className="px-5 py-2 border border-rose-500 text-rose-400 overline hover:bg-rose-500 hover:text-white flex items-center gap-2"
                      data-testid={`admin-reject-${a.id}`}
                    ><X className="w-4 h-4" /> Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
